import React, { Component, type ReactNode } from "react";
import { Alert, Button, Box, Typography, Stack } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { isChunkLoadError } from "../../utils/lazyWithRetry";
import { captureException } from "../../config/sentry";

interface AppErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

/**
 * AppErrorBoundary — Error boundary inside AuthProvider / TenantProvider.
 *
 * Catches unhandled React render errors and displays a user-friendly
 * recovery screen. Specifically handles chunk-load failures from stale
 * deployments with a targeted reload prompt.
 *
 * Positioned inside AuthProvider and TenantProvider so it has access to
 * auth context and can offer richer recovery options ("Try Again").
 * For errors that occur before auth context is available, see
 * RootErrorBoundary which sits above all providers.
 */
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[AppErrorBoundary] Uncaught error:", error, errorInfo);

    // Send to Sentry error tracking
    captureException(error, {
      componentStack: errorInfo.componentStack,
      boundary: "AppErrorBoundary",
    });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch {
      // Firefox restricted context — manual instructions
      this.setState({
        error: new Error(
          "Unable to reload automatically. Please close and reopen this tab.",
        ),
      });
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const isChunkError = error ? isChunkLoadError(error) : false;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          p: 4,
          textAlign: "center",
          backgroundColor: "background.default",
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />

        <Typography variant="h5" gutterBottom fontWeight={600}>
          {isChunkError
            ? "Application Update Available"
            : "Something went wrong"}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 500 }}
        >
          {isChunkError
            ? "A new version of the application has been deployed. Please reload the page to get the latest version."
            : "An unexpected error occurred. You can try reloading the page or going back to the dashboard."}
        </Typography>

        {!isChunkError && error && (
          <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
            {error.message || "An unknown error occurred"}
          </Alert>
        )}

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
            size="large"
          >
            Reload Page
          </Button>

          {!isChunkError && (
            <Button variant="outlined" onClick={this.handleReset} size="large">
              Try Again
            </Button>
          )}
        </Stack>

        {import.meta.env.DEV && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              backgroundColor: "grey.100",
              borderRadius: 1,
              maxWidth: 800,
              width: "100%",
              textAlign: "left",
            }}
          >
            <Typography variant="caption" fontWeight={600} gutterBottom>
              Developer Info:
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                fontSize: "0.7rem",
                wordBreak: "break-word",
              }}
            >
              {error.stack}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }
}
